import React, { useCallback, useEffect, useRef, useState } from 'react';

type ScratchRevealProps = {
  /** Content revealed under the scratch layer */
  children: React.ReactNode;
  /** Cover colour (gold sand default) */
  coverColor?: string;
  /** Fraction of pixels cleared before auto-reveal (0–1) */
  revealThreshold?: number;
  className?: string;
  height?: number;
};

/**
 * Canvas scratch-to-reveal (gold sand / mist). Pointer + touch friendly.
 */
export function ScratchReveal({
  children,
  coverColor = '#C9A227',
  revealThreshold = 0.42,
  className = '',
  height = 280,
}: ScratchRevealProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const drawing = useRef(false);
  const [revealed, setRevealed] = useState(false);

  const paintCover = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const g = ctx.createLinearGradient(0, 0, rect.width, height);
    g.addColorStop(0, coverColor);
    g.addColorStop(0.5, '#E8D48B');
    g.addColorStop(1, '#A67C00');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, rect.width, height);
    // Speckle “sand”
    for (let i = 0; i < 180; i++) {
      ctx.fillStyle = `rgba(255,255,255,${0.08 + Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.arc(Math.random() * rect.width, Math.random() * height, Math.random() * 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    if (rect.width < 8) return;
    ctx.fillStyle = 'rgba(40,30,10,0.62)';
    ctx.font = '600 13px "Source Sans 3", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SCRATCH TO REVEAL', rect.width / 2, height / 2 - 8);
    ctx.font = '400 11px "Source Sans 3", system-ui, sans-serif';
    ctx.fillStyle = 'rgba(40,30,10,0.45)';
    ctx.fillText('Swipe the gold sand', rect.width / 2, height / 2 + 12);
  }, [coverColor, height]);

  useEffect(() => {
    paintCover();
    const onResize = () => {
      if (!revealed) paintCover();
    };
    window.addEventListener('resize', onResize);
    // Card previews often start at width 0 — re-paint when the container gets a real size.
    const ro =
      typeof ResizeObserver !== 'undefined' && containerRef.current
        ? new ResizeObserver(() => {
            if (!revealed) paintCover();
          })
        : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);
    // One rAF pass after mount for wizard aspect-ratio frames
    const raf = window.requestAnimationFrame(() => {
      if (!revealed) paintCover();
    });
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [paintCover, revealed]);

  const scratchAt = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();

    // Sample clearance
    const sample = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let clear = 0;
    const step = 16;
    for (let i = 3; i < sample.data.length; i += 4 * step) {
      if (sample.data[i] < 128) clear++;
    }
    const total = sample.data.length / (4 * step);
    if (total > 0 && clear / total >= revealThreshold) {
      setRevealed(true);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    drawing.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    scratchAt(e.clientX, e.clientY);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    scratchAt(e.clientX, e.clientY);
  };
  const onPointerUp = () => {
    drawing.current = false;
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden rounded-2xl ${className}`}
      style={{ height }}
      data-testid="scratch-reveal"
    >
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      {!revealed && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 z-10 touch-none cursor-crosshair"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          aria-label="Scratch cover to reveal the save the date"
        />
      )}
    </div>
  );
}
